import type { LogRecord, StatusData } from "@fairfleet/geotab";
import { keepPreviousData, useQueries, type UseQueryResult } from "@tanstack/react-query";
import { endOfDay, startOfDay } from "date-fns";
import { useMemo } from "react";
import { type NumericStatusEntry, STATUSDATA_MAX_DRIFT_MS } from "../utils/getLatestStatusValueBefore";
import { mergeTripRangesByDevice, type TripRange } from "../utils/mergeTripRangesByDevice";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { logRecordsByRangeQuery } from "@/hooks/logRecord/useLogRecordsByRange";
import { fetchStatusDataComplete } from "@/utilities/query/fetchStatusDataComplete";

/** Every value a vehicle reported, by vehicle and diagnostic, each series in time order. */
export type RouteStatusSeries = Record<string, Record<string, NumericStatusEntry[]>>;

type DeviceStatus = { deviceId: string; byDiagnostic: Record<string, NumericStatusEntry[]> };

/** Stable module-level combine so TanStack memoizes the merged result and dependants only re-run on data changes. */
function combineLogRecordQueries(results: UseQueryResult<LogRecord[]>[]) {
  return {
    records: results.flatMap(query => query.data ?? []),
    isPending: results.some(query => query.isPending),
    isFetching: results.some(query => query.isFetching),
    error: results.find(query => query.isError)?.error ?? null,
    retryFailed: () => {
      results.forEach(query => {
        if (query.isError) void query.refetch();
      });
    },
  };
}

/** Collects the per-device status results under their vehicle. */
function combineStatusQueries(results: UseQueryResult<DeviceStatus>[]) {
  const series: RouteStatusSeries = {};
  for (const query of results) {
    if (query.data) series[query.data.deviceId] = query.data.byDiagnostic;
  }
  return {
    series,
    isPending: results.some(query => query.isPending),
    error: results.find(query => query.isError)?.error ?? null,
    retryFailed: () => {
      results.forEach(query => {
        if (query.isError) void query.refetch();
      });
    },
  };
}

/**
 * Per vehicle, from its first trip's start to its last trip's end — not whole days like the
 * positions: the point details offer what was reported during the trips, and all diagnostics of
 * a lorry's day are tens of thousands of values. Starts a little early, as far back as a value may
 * still count for a point (see {@link STATUSDATA_MAX_DRIFT_MS}).
 */
export function statusWindowsByDevice(ranges: TripRange[]): Map<string, { from: Date; to: Date }> {
  const windows = new Map<string, { from: Date; to: Date }>();
  for (const range of ranges) {
    if (!range.deviceId) continue;
    const from = new Date(range.start.getTime() - STATUSDATA_MAX_DRIFT_MS);
    const existing = windows.get(range.deviceId);
    windows.set(range.deviceId, {
      from: existing && existing.from < from ? existing.from : from,
      to: existing && existing.to > range.stop ? existing.to : range.stop,
    });
  }
  return windows;
}

function groupByDiagnostic(rows: StatusData[]): Record<string, NumericStatusEntry[]> {
  const byDiagnostic: Record<string, NumericStatusEntry[]> = {};
  for (const row of rows) {
    const diagnosticId = row.diagnostic?.id;
    if (!diagnosticId || typeof row.data !== "number") continue;
    (byDiagnostic[diagnosticId] ??= []).push({ data: row.data, dateTime: new Date(row.dateTime) });
  }
  for (const entries of Object.values(byDiagnostic)) {
    entries.sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime());
  }
  return byDiagnostic;
}

/**
 * LogRecords of the selected trips' vehicles, and every status value those vehicles reported in the
 * same windows — the point details pick from these. `fromDate`/`toDate` bound the queries when no
 * trip is selected.
 */
export function useRouteLogRecords(tripRanges: TripRange[], fromDate: Date, toDate: Date) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  const fullRange = useMemo(() => {
    if (!tripRanges.length) return { from: startOfDay(fromDate), to: endOfDay(toDate) };

    const first = tripRanges.reduce((min, range) => Math.min(min, range.start.getTime()), Infinity);
    const last = tripRanges.reduce((max, range) => Math.max(max, range.stop.getTime()), -Infinity);
    return { from: startOfDay(new Date(first)), to: endOfDay(new Date(last)) };
  }, [fromDate, toDate, tripRanges]);

  const deviceIds = useMemo(
    () => [...new Set(tripRanges.map(range => range.deviceId).filter((id): id is string => !!id))],
    [tripRanges],
  );
  const deviceRanges = useMemo(() => mergeTripRangesByDevice(tripRanges), [tripRanges]);
  const statusWindows = useMemo(() => statusWindowsByDevice(tripRanges), [tripRanges]);

  const logRecords = useQueries({
    queries: deviceIds.map(deviceId => {
      const range = deviceRanges.get(deviceId) ?? fullRange;
      return {
        ...logRecordsByRangeQuery(geotabClient, activeSessionId, deviceId, range.from, range.to),
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        enabled: tripRanges.length > 0,
      };
    }),
    combine: combineLogRecordQueries,
  });

  // One query per vehicle and its own window, for all of its diagnostics at once: a trip of two
  // hours is a few thousand values (2,547 for a lorry), and the point details may show any of them.
  const status = useQueries({
    queries: deviceIds.map(deviceId => {
      const range = statusWindows.get(deviceId) ?? fullRange;
      return {
        queryKey: ["routeStatusSeries", activeSessionId, deviceId, range.from.getTime(), range.to.getTime()],
        queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<DeviceStatus> => {
          const rows: StatusData[] = [];
          await fetchStatusDataComplete(geotabClient, {
            tasks: [{ key: deviceId, deviceId }],
            fromDate: range.from,
            toDate: range.to,
            signal,
            onPage: (_key, page) => rows.push(...page),
          });
          return { deviceId, byDiagnostic: groupByDiagnostic(rows) };
        },
        refetchOnWindowFocus: false,
      };
    }),
    combine: combineStatusQueries,
  });

  return {
    records: logRecords.records,
    statusSeries: status.series,
    isLoading: logRecords.isPending,
    isFetching: logRecords.isFetching,
    error: logRecords.error,
    retry: logRecords.retryFailed,
    statusLoading: status.isPending,
    statusError: status.error,
    retryStatus: status.retryFailed,
  };
}
